import { useEffect, useState } from "react";
import { query } from "./dataClient.js";
import { encodeScopePath } from "./dimensionDefs.js";

// usePositionFlows — the vertical sankey between stacked Position levels. Two
// kinds of ribbon, both in the scope-columns flow language:
//
//   • SPINE — your selected member at level k → your selected member at level
//     k+1. Because level k+1 is already scoped INTO your level-k selection, this
//     flow IS the next level's own distribution — so its weight is just the
//     selected member's activity, known without a fetch. The spine is the thread
//     of "where you are" and always draws, even before cross-links resolve.
//
//   • CROSS-LINKS — from the other (sibling) members of level k to level k+1's
//     members: |level_k.prefix ∩ A ∩ B|. Fetched the SAME way the scope columns
//     fetch flow links: one batched co-occurrence query per adjacent level pair
//     (getFlowLinks → runAnalyticsFlowPair groups (level_k.dim, level_{k+1}.dim)
//     within level_k.prefix, top sources as the a-axis IN, next level's members
//     as the b-axis IN) — replacing one re-scope getDimensionEntries per source
//     member. Only rendered where both endpoints are on-screen (the overlay maps
//     member id → visible cell).
//
// `sequence` is the levels in display order — committed rows then the projected
// descent — each { dim, prefix, members:[{id,value}], selectedId }. Flows are
// keyed by member id (NOT row index) so paging a row doesn't invalidate them.
// buildSpineFlows / fetchCrossLinkFlows are pure (query-injected) and unit-tested
// in usePositionFlows.test.js.

const SRC_K = 6;     // sibling sources fanned per level for cross-links

/**
 * The selected→selected thread down the stack. Synchronous, no network: each
 * level's selected-member activity IS the spine weight into it.
 * @param {Array<{dim, prefix, members:Array<{id,value}>, selectedId}>} sequence
 * @returns {Array<{pair, aId, bId, count, spine:true}>}
 */
export function buildSpineFlows(sequence) {
  const seq = sequence || [];
  const spine = [];
  for (let k = 0; k < seq.length - 1; k++) {
    const A = seq[k].selectedId;
    const B = seq[k + 1].selectedId;
    if (A == null || B == null) continue;
    const bMember = (seq[k + 1].members || []).find((m) => String(m.id) === String(B));
    spine.push({ pair: k, aId: String(A), bId: String(B), count: Math.max(0, Number(bMember?.value) || 0), spine: true });
  }
  return spine;
}

/**
 * The sibling cross-links: fan the top-K sources of each level to the next
 * level's dim via the re-scope query. Excludes the selected→selected pair (the
 * spine, already synthesized). Best-effort — a failed fetch drops that source.
 * @param {{sequence:Array, range:*, query:Function, signal?:{cancelled:boolean}}} args
 * @returns {Promise<Array<{pair, aId, bId, count, spine:false}>>}
 */
export async function fetchCrossLinkFlows({ sequence, range, query: q, signal }) {
  const seq = sequence || [];
  if (seq.length < 2) return [];
  const cross = [];
  await Promise.all(seq.slice(0, -1).map(async (lv, k) => {
    const tgtDim = seq[k + 1].dim;
    const selId = String(lv.selectedId);
    const nextSel = String(seq[k + 1].selectedId);
    const srcIds = new Set((lv.members || []).slice(0, SRC_K).map((m) => String(m.id)));
    srcIds.add(selId);
    const bIds = (seq[k + 1].members || []).map((m) => String(m.id));
    if (!bIds.length) return;
    // One batched co-occurrence query per adjacent level pair, scoped to this
    // level's prefix — the a-axis IN (sources) does the per-source split the old
    // per-member fetch did, in a single query.
    const res = await q(
      "getFlowLinks",
      { scopePath: lv.prefix || [], pairs: [{ aDim: lv.dim, aIds: [...srcIds], bDim: tgtDim, bIds }], range, scoped: true },
      { cache: true },
    ).catch((e) => { console.warn("[usePositionFlows] cross-link fetch failed", e); return null; });
    if (signal?.cancelled || !res) return;
    for (const link of res.pairs?.[0]?.links || []) {
      const aId = String(link.aId);
      const bId = String(link.bId);
      if (aId === selId && bId === nextSel) continue; // the spine, already synthesized
      cross.push({ pair: k, aId, bId, count: Math.max(0, Number(link.count) || 0), spine: false });
    }
  }));
  return cross;
}

export function usePositionFlows(sequence, range) {
  const [flows, setFlows] = useState([]);
  const sig = JSON.stringify(
    (sequence || []).map((lv) => [lv.dim, encodeScopePath(lv.prefix || []), lv.selectedId, (lv.members || []).slice(0, SRC_K).map((m) => String(m.id))]),
  );

  useEffect(() => {
    const seq = sequence || [];
    if (seq.length < 2) { setFlows([]); return undefined; }
    const signal = { cancelled: false };

    // Spine first, synchronously — it needs no network, so the descent thread is
    // visible immediately while the cross-links fetch.
    const spine = buildSpineFlows(seq);
    setFlows(spine);

    fetchCrossLinkFlows({ sequence: seq, range, query, signal })
      .then((cross) => { if (!signal.cancelled) setFlows([...spine, ...cross]); })
      .catch((e) => { if (!signal.cancelled) { console.error("[usePositionFlows]", e); setFlows(spine); } });

    return () => { signal.cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, range]);

  return flows;
}
